const TICKED_PROMPTS_STORAGE_KEY = 'positive_ticked_prompts';
const REMINDER_STORAGE_KEY = 'positive_reminder';
const EXPIRATION_DAYS = 90; // 3 months

class StateService {
  constructor() {
    this.tickedPrompts = {};
    this.listeners = new Set();
    this.loadFromStorage();
  }

  // Load ticked prompts from localStorage, filtering out expired entries
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(TICKED_PROMPTS_STORAGE_KEY);
      if (!stored) {
        this.tickedPrompts = {};
        return;
      }

      const data = JSON.parse(stored);
      const now = Date.now();
      const expirationMs = EXPIRATION_DAYS * 24 * 60 * 60 * 1000;

      // Filter out expired entries and convert to simple format
      const filtered = {};
      for (const [dayKey, entry] of Object.entries(data)) {
        if (entry.savedAt && (now - entry.savedAt) < expirationMs) {
          filtered[dayKey] = entry.ticks;
        }
      }
      this.tickedPrompts = filtered;
    } catch (e) {
      console.error('Failed to load ticked prompts from localStorage:', e);
      this.tickedPrompts = {};
    }
  }

  // Save ticked prompts to localStorage with timestamps
  saveToStorage() {
    try {
      const now = Date.now();
      const expirationMs = EXPIRATION_DAYS * 24 * 60 * 60 * 1000;

      // Load existing data to preserve timestamps for unchanged entries
      const stored = localStorage.getItem(TICKED_PROMPTS_STORAGE_KEY);
      const existingData = stored ? JSON.parse(stored) : {};

      // Build new storage object
      const data = {};
      for (const [dayKey, ticks] of Object.entries(this.tickedPrompts)) {
        // Skip if all ticks are false (no need to store empty entries)
        if (ticks.every(t => !t)) continue;

        // Preserve existing timestamp if entry exists and is not expired
        const existingEntry = existingData[dayKey];
        if (existingEntry && existingEntry.savedAt && (now - existingEntry.savedAt) < expirationMs) {
          data[dayKey] = { ticks, savedAt: existingEntry.savedAt };
        } else {
          data[dayKey] = { ticks, savedAt: now };
        }
      }

      localStorage.setItem(TICKED_PROMPTS_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save ticked prompts to localStorage:', e);
    }
  }

  // Get ticked prompts state
  getTickedPrompts() {
    return this.tickedPrompts;
  }

  // Check if a specific prompt is ticked for a given day
  isPromptTicked(dayKey, promptIndex) {
    return this.tickedPrompts[dayKey]?.[promptIndex] || false;
  }

  // Toggle a prompt's ticked state
  togglePromptTicked(dayKey, promptIndex) {
    const dayTicks = this.tickedPrompts[dayKey] || [false, false, false];
    const newDayTicks = [...dayTicks];
    newDayTicks[promptIndex] = !newDayTicks[promptIndex];

    this.tickedPrompts = { ...this.tickedPrompts, [dayKey]: newDayTicks };
    this.saveToStorage();
    this.notifyListeners();

    return newDayTicks[promptIndex]; // Return the new ticked state
  }

  // Compute day-of-year for a given offset from today
  getDayOfYear(offset = 0) {
    const now = new Date();
    now.setDate(now.getDate() + offset);
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now - start;
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  }

  // Check if all 3 prompts are ticked for a given day key
  isDayCompleted(dayKey) {
    const ticks = this.tickedPrompts[dayKey];
    return Array.isArray(ticks) && ticks.length === 3 && ticks.every(Boolean);
  }

  // Calculate the current streak (consecutive completed days)
  getStreak() {
    let streak = 0;
    const todayKey = this.getDayOfYear(0);
    const startOffset = this.isDayCompleted(todayKey) ? 0 : -1;

    for (let i = startOffset; i > startOffset - EXPIRATION_DAYS; i--) {
      const dayKey = this.getDayOfYear(i);
      if (this.isDayCompleted(dayKey)) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  }

  // Reminder time management
  getReminderTime() {
    try {
      return localStorage.getItem(REMINDER_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  setReminderTime(time) {
    try {
      if (time) {
        localStorage.setItem(REMINDER_STORAGE_KEY, time);
      } else {
        localStorage.removeItem(REMINDER_STORAGE_KEY);
      }
    } catch (e) {
      console.error('Failed to save reminder time:', e);
    }
  }

  // Subscribe to state changes
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Notify all listeners of state change
  notifyListeners() {
    this.listeners.forEach(listener => listener(this.tickedPrompts));
  }
}

// Export singleton instance
export default new StateService();
